import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, ExternalLink, Mail, MapPin, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { adminListProposals } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminProposals() {
  const [proposals, setProposals] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    adminListProposals().then((d) => setProposals(d.proposals || [])).catch(() => { toast.error("Failed to load proposals"); setProposals([]); });
  }, []);

  const openPdf = (p) => window.open(`${BACKEND_URL}${p.pdf_url}`, "_blank", "noopener");

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
        <div className="flex items-center gap-2">
          <Sparkles size={22} className="text-brand" />
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900">AI Growth Advisor Proposals</h1>
        </div>
        <p className="text-slate-500 mt-1">Every branded proposal generated by the AI Growth Advisor — with its CRM lead and downloadable PDF.</p>

        {proposals === null ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-brand" size={26} /></div>
        ) : proposals.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-10 mt-8 text-center text-slate-400" data-testid="proposals-empty">
            No proposals yet. They appear here when visitors request one from the AI Growth Advisor.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
            <div className="lg:col-span-2 space-y-3">
              {proposals.map((p) => (
                <button key={p.id} onClick={() => setActive(p)} data-testid={`proposal-row-${p.id}`}
                  className={`w-full text-left bg-white border rounded-xl card-shadow p-4 flex items-center justify-between gap-4 transition-colors ${active?.id === p.id ? "border-brand ring-1 ring-brand/20" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{p.name || p.email}</div>
                    <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Mail size={11} /> {p.email}</span>
                      {p.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.location}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {p.bundle && <span className="text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-brand px-2 py-0.5 rounded-full">{p.bundle}</span>}
                    <div className="text-[11px] text-slate-400 mt-1">{p.tenant === "kerala" ? "Kerala CRM" : "Main CRM"}</div>
                  </div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-1">
              {active ? (
                <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 sticky top-6" data-testid="proposal-detail">
                  <div className="font-heading text-lg font-bold text-slate-900">{active.headline || active.bundle || "Growth Plan"}</div>
                  {(active.business_type || active.location) && (
                    <div className="text-xs text-slate-400 mt-1">{[active.business_type, active.location].filter(Boolean).join(" · ")}</div>
                  )}
                  {active.created_at && (
                    <div className="text-xs text-slate-400 mt-0.5">Requested {new Date(active.created_at).toLocaleDateString()}</div>
                  )}
                  {active.bundle && <span className="inline-block mt-2 text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-brand px-2 py-0.5 rounded-full">{active.bundle}</span>}
                  {active.summary && <p className="text-sm text-slate-500 mt-3">{active.summary}</p>}
                  {active.recommended?.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Recommended</div>
                      <ul className="space-y-1.5">
                        {active.recommended.map((r, i) => (
                          <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-brand">•</span> {r.label || r.slug}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button onClick={() => openPdf(active)} data-testid="proposal-download" className="btn-primary rounded-xl px-5 py-3 mt-6 w-full flex items-center justify-center gap-2 text-sm">
                    <FileText size={16} /> View / Download PDF <ExternalLink size={13} />
                  </button>
                  <div className="text-[11px] text-slate-400 mt-3">Contact: {active.email}{active.phone ? ` · ${active.phone}` : ""}</div>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 text-center text-slate-400 text-sm">Select a proposal to view details.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
